
import React, { useState, useEffect, useCallback, useRef } from "react";
import { OrderServices } from "../../services/OrderServices";

// ── Mobile hook ───────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 640;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e) => setIsMobile(e.matches);

    setIsMobile(mq.matches);

    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }

    // Safari fallback
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  return isMobile;
};

// ── Constants ─────────────────────────────────────────────────────────────────
const ORDER_STATUS = {
  pending:    { label: "Pending",    icon: "ph-clock",                   cls: "pending"    },
  processing: { label: "Processing", icon: "ph-gear",                    cls: "processing" },
  shipped:    { label: "Shipped",    icon: "ph-truck",                   cls: "shipped"    },
  delivered:  { label: "Delivered",  icon: "ph-check-circle",            cls: "delivered"  },
  cancelled:  { label: "Cancelled",  icon: "ph-x-circle",                cls: "cancelled"  },
  refunded:   { label: "Refunded",   icon: "ph-arrow-counter-clockwise", cls: "refunded"   },
  failed:     { label: "Failed",     icon: "ph-warning-circle",          cls: "failed"     },
};

const PAYMENT_STATUS = {
  paid:     { label: "Paid",     cls: "paid"     },
  unpaid:   { label: "Unpaid",   cls: "unpaid"   },
  failed:   { label: "Failed",   cls: "failed"   },
  refunded: { label: "Refunded", cls: "refunded" },
};

const PAYMENT_METHOD_LABEL = {
  stripe:        "Credit / Debit Card",
  cod:           "Cash on Delivery",
  bank_transfer: "Bank Transfer",
};

const TRACKING_STAGES = [
  { key: "pending",    label: "Order Placed", icon: "ph-check-circle" },
  { key: "processing", label: "Processing",   icon: "ph-gear"         },
  { key: "shipped",    label: "Shipped",      icon: "ph-truck"        },
  { key: "delivered",  label: "Delivered",    icon: "ph-package"      },
];

const STAGE_SUB = {
  pending:    { pending: "Waiting for confirmation", processing: "Confirmed",          shipped: "Confirmed",            delivered: "Confirmed"              },
  processing: { processing: "Preparing your order",  shipped: "Ready for shipment",   delivered: "Packed & dispatched"                                     },
  shipped:    { shipped: "On its way to you",         delivered: "Arrived at destination"                                                                   },
  delivered:  { delivered: "Successfully delivered"                                                                                                         },
};

const CANCELLED_CFG = {
  cancelled: { msg: "This order was cancelled. If you were charged, a refund is on its way."          },
  refunded:  { msg: "Your refund has been processed and should appear within 5–10 business days."     },
  failed:    { msg: "Something went wrong with this order. Please contact support."                   },
};

const FOOTER_CFG = {
  pending:    { cls: "warn",    icon: "ph-clock",        text: "Awaiting confirmation"                },
  processing: { cls: "info",    icon: "ph-gear",         text: "Preparing your order for shipment"   },
  shipped:    { cls: "info",    icon: "ph-truck",        text: "Your order is on its way"             },
  delivered:  { cls: "success", icon: "ph-check-circle", text: "Delivered successfully"               },
};

const FILTERS = [
  { key: "all",        label: "All Orders"  },
  { key: "pending",    label: "Pending"     },
  { key: "processing", label: "Processing"  },
  { key: "shipped",    label: "Shipped"     },
  { key: "delivered",  label: "Delivered"   },
  { key: "cancelled",  label: "Cancelled"   },
  { key: "refunded",   label: "Refunded"    },
  { key: "failed",     label: "Failed"      },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";
const formatTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "";

// ── Status Badge ──────────────────────────────────────────────────────────────
const Badge = ({ cls, label, icon }) => (
  <span className={`mo-badge mo-badge--${cls}`}>
    {icon && <i className={`ph ${icon}`} />}
    {label}
  </span>
);

// ── Mobile Dropdown Filter ────────────────────────────────────────────────────
const StatusDropdown = ({ filter, setFilter, statusCounts }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, []);

  const current = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const oStatus = filter !== "all" ? ORDER_STATUS[filter] : null;

  return (
    <div ref={ref} className="mo-dropdown">
      <button
        className="mo-dropdown__btn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="mo-dropdown__left">
          {oStatus
            ? <span className={`mo-status-dot mo-status-dot--${filter}`} />
            : <i className="ph ph-funnel" />}
          <span className="mo-dropdown__label">{current.label}</span>
          {filter !== "all" && statusCounts[filter] > 0 && (
            <span className="mo-dropdown__count">{statusCounts[filter]}</span>
          )}
        </div>
        <i className={`ph ph-caret-down mo-dropdown__chevron ${open ? "mo-dropdown__chevron--open" : ""}`} />
      </button>

      {open && (
        <div className="mo-dropdown__panel" role="listbox" aria-label="Filter by status">
          {FILTERS.map((f) => {
            const st       = f.key !== "all" ? ORDER_STATUS[f.key] : null;
            const isActive = filter === f.key;
            const count    = f.key !== "all" ? (statusCounts[f.key] || 0) : null;
            return (
              <button
                key={f.key}
                role="option"
                aria-selected={isActive}
                className={`mo-dropdown__item ${isActive ? "mo-dropdown__item--active" : ""}`}
                onClick={() => { setFilter(f.key); setOpen(false); }}
              >
                <div className="mo-dropdown__item-left">
                  {st
                    ? <span className={`mo-status-dot mo-status-dot--${f.key}`} />
                    : <i className="ph ph-list" />}
                  <span>{f.label}</span>
                </div>
                <div className="mo-dropdown__item-right">
                  {count !== null && count > 0 && (
                    <span className={`mo-dropdown__item-count ${isActive ? "mo-dropdown__item-count--active" : ""}`}>
                      {count}
                    </span>
                  )}
                  {isActive && <i className="ph ph-check mo-dropdown__check" />}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Tracking Timeline — Vertical Stepper ──────────────────────────────────────
const TrackingTimeline = ({ order }) => {
  const oStatus     = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
  const isCancelled = ["cancelled", "failed", "refunded"].includes(order.status);
  const activeIdx   = TRACKING_STAGES.findIndex((s) => s.key === order.status);
  const safeIdx     = activeIdx < 0 ? 0 : activeIdx;

  // ── Cancelled / failed / refunded view ──
  if (isCancelled) {
    const cfg = CANCELLED_CFG[order.status] || CANCELLED_CFG.cancelled;
    return (
      <div className="mo-timeline">
        <div className={`mo-tl-alert mo-tl-alert--${order.status}`}>
          <i className={`ph ${oStatus.icon}`} />
          <div>
            <div className="mo-tl-alert__title">{oStatus.label}</div>
            <div className="mo-tl-alert__sub">{cfg.msg}</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal vertical stepper view ──
  const footer = FOOTER_CFG[order.status] || FOOTER_CFG.pending;

  return (
    <div className="mo-timeline">
      <div className="mo-stepper">
        {TRACKING_STAGES.map((stage, i) => {
          const isDone   = i < safeIdx;
          const isActive = i === safeIdx;
          const isLast   = i === TRACKING_STAGES.length - 1;
          const sub      = STAGE_SUB[stage.key]?.[order.status] || "";

          return (
            <div key={stage.key} className="mo-step">
              {/* Left column: dot + vertical connector */}
              <div className="mo-step__line-wrap">
                <div
                  className={[
                    "mo-step__dot",
                    isDone   ? "mo-step__dot--done"   : "",
                    isActive ? "mo-step__dot--active" : "",
                  ].join(" ")}
                >
                  <i className={`ph ${isDone ? "ph-check" : stage.icon}`} />
                </div>
                {!isLast && (
                  <div className={`mo-step__connector ${isDone ? "mo-step__connector--filled" : ""}`} />
                )}
              </div>

              {/* Right column: label + sub-status */}
              <div className={`mo-step__content ${isLast ? "mo-step__content--last" : ""}`}>
                <div
                  className={[
                    "mo-step__label",
                    isDone   ? "mo-step__label--done"   : "",
                    isActive ? "mo-step__label--active" : "",
                  ].join(" ")}
                >
                  {stage.label}
                </div>
                {(isDone || isActive) && sub && (
                  <div className={`mo-step__sub ${isActive ? "mo-step__sub--active" : ""}`}>
                    {sub}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer summary pill */}
      <div className={`mo-tl-footer mo-tl-footer--${footer.cls}`}>
        <i className={`ph ${footer.icon}`} />
        {footer.text}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const MyOrders = ({ onBack }) => {
  const isMobile = useIsMobile();
  const rootRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const [cancelingId, setCancelingId] = useState(null);
  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [expandedId,    setExpandedId]    = useState(null);
  const [filter,        setFilter]        = useState("all");
  const [trackingError, setTrackingError] = useState(null);
  const [refreshing,    setRefreshing]    = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await OrderServices.getMyOrders();
      setOrders(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
    } catch {
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders();
    setRefreshing(false);
  };

  const toggleExpand = (id) => setExpandedId((p) => (p === id ? null : id));

  const handleTrackShipment = async (orderId) => {
    setTrackingError(null);
    try {
      const res = await OrderServices.getShipmentUrl(orderId);
      if (!res?.shipment_url) { setTrackingError("Tracking URL not available"); return; }
      window.open(res.shipment_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setTrackingError(err?.response?.data?.message || err?.message || "Failed to open tracking link");
    }
  };
  const canShowCancelAction = (order) => {
    const status = String(order?.status || "").toLowerCase();
    const paymentStatus = String(order?.payment_status || "").toLowerCase();

    // Normal cancellable orders
    if (["pending", "processing"].includes(status)) return true;

    // Old orders: cancelled + unpaid should show Delete Order button
    if (status === "cancelled" && paymentStatus === "unpaid") return true;

    return false;
  };

  const getCancelActionLabel = (order) => {
    const status = String(order?.status || "").toLowerCase();
    const paymentStatus = String(order?.payment_status || "").toLowerCase();

    if (status === "cancelled" && paymentStatus === "unpaid") {
      return "Delete Order";
    }

    return "Cancel Order";
  };

  const handleCancelOrder = async (order) => {
    if (!order?.id || cancelingId) return;

    const status = String(order?.status || "").toLowerCase();
    const paymentStatus = String(order?.payment_status || "").toLowerCase();
    const isPaid = paymentStatus === "paid";
    const isOldCancelledUnpaid = status === "cancelled" && paymentStatus === "unpaid";

    const ok = window.confirm(
      isOldCancelledUnpaid
        ? "Are you sure you want to delete this cancelled unpaid order? It will be permanently removed from your order list."
        : isPaid
        ? "Are you sure you want to cancel this paid order? It will remain visible as Cancelled."
        : "Are you sure you want to cancel this unpaid order? It will be permanently deleted from your order list."
    );

    if (!ok) return;

    setCancelingId(order.id);
    setError("");

    try {
      const res = await OrderServices.cancelOrder(order.id);

      if (res?.deleted) {
        setOrders((prev) => prev.filter((o) => o.id !== order.id));
        setExpandedId((prev) => (prev === order.id ? null : prev));
      } else {
        setOrders((prev) =>
          prev.map((o) =>
            o.id === order.id
              ? {
                  ...o,
                  ...(res?.order || {}),
                  status: "cancelled",
                }
              : o
          )
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to cancel order. Please try again."
      );
    } finally {
      setCancelingId(null);
    }
  };

  const filtered     = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const totalSpent   = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + Number(o.total || 0), 0);
  const statusCounts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

  return (
    <>
      <style>{CSS}</style>

      <div className="mo-root" ref={rootRef}>

        {/* ── Topbar ── */}
        <div className="mo-topbar">
          <button className="mo-back-btn" onClick={onBack}>
            <i className="ph ph-arrow-left" />
            Back to Overview
          </button>
          <button className="mo-refresh-btn" onClick={handleRefresh} disabled={loading || refreshing}>
            <i className={`ph ph-arrows-clockwise ${refreshing ? "mo-spin" : ""}`} />
            {refreshing ? "Loading…" : "Refresh"}
          </button>
        </div>

        {/* ── Page Header ── */}
        <div className="mo-page-header">
          <div className="mo-page-title">My Orders</div>
          <div className="mo-page-sub">View and track all your purchases</div>
        </div>

        {/* ── Stats ── */}
        {!loading && orders.length > 0 && (
          <div className="mo-stats">
            <div className="mo-stat">
              <div className="mo-stat__icon mo-stat__icon--blue"><i className="ph ph-package" /></div>
              <div>
                <div className="mo-stat__val">{orders.length}</div>
                <div className="mo-stat__lbl">Total Orders</div>
              </div>
            </div>
            <div className="mo-stat">
              <div className="mo-stat__icon mo-stat__icon--green"><i className="ph ph-check-circle" /></div>
              <div>
                <div className="mo-stat__val">{statusCounts.delivered || 0}</div>
                <div className="mo-stat__lbl">Delivered</div>
              </div>
            </div>
            <div className="mo-stat">
              <div className="mo-stat__icon mo-stat__icon--amber"><i className="ph ph-clock" /></div>
              <div>
                <div className="mo-stat__val">{(statusCounts.pending || 0) + (statusCounts.processing || 0)}</div>
                <div className="mo-stat__lbl">In Progress</div>
              </div>
            </div>
            <div className="mo-stat">
              <div className="mo-stat__icon mo-stat__icon--purple"><i className="ph ph-currency-dollar" /></div>
              <div>
                <div className="mo-stat__val">${totalSpent.toFixed(2)}</div>
                <div className="mo-stat__lbl">Total Spent</div>
              </div>
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="mo-error-alert">
            <i className="ph ph-warning-circle" />
            {error}
          </div>
        )}

        {/* ── Orders Section ── */}
        <div className="mo-section">
          <div className="mo-section__header">
            <div>
              <div className="mo-section__title">Order History</div>
              <div className="mo-section__count">
                {filtered.length} order{filtered.length !== 1 ? "s" : ""}
                {filter !== "all" ? ` · ${FILTERS.find((f) => f.key === filter)?.label}` : ""}
              </div>
            </div>
          </div>

          {/* Desktop filter chips */}
          <div className="mo-chips-wrap" aria-label="Filter orders by status" role="group">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={`mo-chip ${filter === f.key ? "mo-chip--active" : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                {f.key !== "all" && statusCounts[f.key] > 0 && (
                  <span className="mo-chip__count">{statusCounts[f.key]}</span>
                )}
              </button>
            ))}
          </div>

          {/* Mobile dropdown */}
          <div className="mo-dropdown-wrap">
            <StatusDropdown filter={filter} setFilter={setFilter} statusCounts={statusCounts} />
          </div>

          {/* Loading */}
          {loading && (
            <div className="mo-spinner">
              <div className="mo-spinner__ring" />
              Loading your orders…
            </div>
          )}

          {/* Empty */}
          {!loading && filtered.length === 0 && (
            <div className="mo-empty">
              <i className="ph ph-package mo-empty__icon" />
              <div className="mo-empty__title">
                {filter === "all" ? "No orders yet" : `No ${filter} orders`}
              </div>
              <div className="mo-empty__sub">
                {filter === "all"
                  ? "When you place an order, it will appear here."
                  : `You don't have any ${filter} orders right now.`}
              </div>
            </div>
          )}

          {/* ═══ Order Cards ═══ */}
          {!loading && filtered.map((order) => {
            const expanded = expandedId === order.id;
            const oStatus  = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
            const pStatus  = PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.unpaid;
            const items    = order.items || [];

            return (
              <div
                key={order.id}
                className={`mo-card ${expanded ? "mo-card--expanded" : ""}`}
                id={`mo-card-${order.id}`}
              >
                {/* ── Card Header ── */}
                <div
                  className="mo-card__header"
                  onClick={() => toggleExpand(order.id)}
                  role="button"
                  aria-expanded={expanded}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleExpand(order.id)}
                >
                  <div className="mo-card__order">
                    <div className="mo-field-label">Order</div>
                    <div className="mo-order-id">#{order.id}</div>
                  </div>

                  <div className="mo-card__meta">
                    <div>
                      <div className="mo-field-label">Placed</div>
                      <div className="mo-meta-val">{formatDate(order.created_at)}</div>
                    </div>
                    <div>
                      <div className="mo-field-label">Items</div>
                      <div className="mo-meta-val">{items.length} item{items.length !== 1 ? "s" : ""}</div>
                    </div>
                  </div>

                  <div className="mo-card__total-wrap">
                    <div>
                      <div className="mo-field-label">Total</div>
                      <div className="mo-card__total">${Number(order.total || 0).toFixed(2)}</div>
                    </div>
                    <i className={`ph ph-caret-down mo-card__chevron ${expanded ? "mo-card__chevron--open" : ""}`} />
                  </div>

                  <div className="mo-card__badges">
                    <Badge cls={oStatus.cls} label={oStatus.label} icon={oStatus.icon} />
                    <Badge cls={pStatus.cls} label={pStatus.label} />
                  </div>
                </div>

                {/* ── Expanded Body ── */}
                {expanded && (
                  <div className="mo-card__body">

                    {/* Vertical stepper tracking */}
                    <TrackingTimeline order={order} />

                    {/* Detail grid */}
                    <div className="mo-detail-grid">
                      <div>
                        <div className="mo-field-label">Order Date</div>
                        <div className="mo-detail-val">{formatDate(order.created_at)}</div>
                        <div className="mo-detail-sub">{formatTime(order.created_at)}</div>
                      </div>
                      <div>
                        <div className="mo-field-label">Payment Method</div>
                        <div className="mo-detail-val mo-detail-val--sm">
                          {PAYMENT_METHOD_LABEL[order.payment_method] || order.payment_method}
                        </div>
                      </div>
                      <div>
                        <div className="mo-field-label">Payment Status</div>
                        <Badge cls={pStatus.cls} label={pStatus.label} />
                      </div>
                      <div>
                        <div className="mo-field-label">Order Status</div>
                        <Badge cls={oStatus.cls} label={oStatus.label} icon={oStatus.icon} />
                      </div>
                      {order.coupon_code && (
                        <div>
                          <div className="mo-field-label">Coupon Used</div>
                          <div className="mo-detail-val mo-detail-val--coupon">{order.coupon_code}</div>
                        </div>
                      )}
                      <div>
                        <div className="mo-field-label">Email</div>
                        <div className="mo-detail-val mo-detail-val--email">{order.email}</div>
                      </div>
                      <div>
                        <div className="mo-field-label">Phone</div>
                        <div className="mo-detail-val">{order.phone}</div>
                      </div>
                    </div>

                    {canShowCancelAction(order) && (
                      <div className="mo-block">
                        <button
                          type="button"
                          className="mo-cancel-btn"
                          disabled={cancelingId === order.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelOrder(order);
                          }}
                        >
                          <i
                            className={`ph ${
                              cancelingId === order.id
                                ? "ph-spinner-gap mo-spin"
                                : getCancelActionLabel(order) === "Delete Order"
                                ? "ph-trash"
                                : "ph-x-circle"
                            }`}
                          />
                          {cancelingId === order.id ? "Processing..." : getCancelActionLabel(order)}
                        </button>
                      </div>
                    )}

                    {/* Shipping address */}
                    <div className="mo-block">
                      <div className="mo-block__label">
                        <i className="ph ph-map-pin" /> Shipping Address
                      </div>
                      <div className="mo-info-box">
                        <strong>{order.first_name} {order.last_name}</strong>
                        {order.business_name && (
                          <span className="mo-info-box__company"> · {order.business_name}</span>
                        )}
                        <br />{order.address}
                        {order.address_line2 && `, ${order.address_line2}`}
                        <br />{order.city}, {order.state} {order.post_code}
                        <br />{order.country}
                      </div>
                    </div>

                    {/* Shipment tracking */}
                    {order.track_code && order.shipping_track && (
                      <div className="mo-block">
                        <div className="mo-block__label">
                          <i className="ph ph-package" /> Shipment Tracking
                        </div>
                        <div className="mo-shiptrack-box">
                          <div className="mo-shiptrack-info">
                            <div className="mo-shiptrack-name">{order.shipping_track.shipment_name}</div>
                            <div className="mo-shiptrack-code">
                              Tracking code:{" "}
                              <strong className="mo-shiptrack-code__val">{order.track_code}</strong>
                            </div>
                          </div>
                          <button
                            className="mo-track-btn"
                            onClick={(e) => { e.stopPropagation(); handleTrackShipment(order.id); }}
                          >
                            <i className="ph ph-truck" /> Track Package
                          </button>
                        </div>
                        {trackingError && (
                          <div className="mo-track-error">{trackingError}</div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {order.notes && (
                      <div className="mo-block">
                        <div className="mo-block__label">
                          <i className="ph ph-note" /> Order Notes
                        </div>
                        <div className="mo-info-box mo-info-box--italic">{order.notes}</div>
                      </div>
                    )}

                    {/* Items list */}
                    {items.length > 0 && (
                      <>
                        <div className="mo-items-header">
                          <i className="ph ph-shopping-bag" /> Order Items ({items.length})
                        </div>
                        {items.map((item, idx) => (
                          <div key={item.id || idx} className="mo-item">
                            <div className="mo-item__left">
                              <div className="mo-item__icon"><i className="ph ph-cube" /></div>
                              <div className="mo-item__info">
                                <div className="mo-item__name">{item.product_title || "Product"}</div>
                                {item.variant_value && (
                                  <div className="mo-item__variant">Variant: {item.variant_value}</div>
                                )}
                              </div>
                            </div>
                            <div className="mo-item__right">
                              <div className="mo-item__price">${Number(item.unit_price || 0).toFixed(2)}</div>
                              <div className="mo-item__qty">Qty: {item.quantity}</div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Totals */}
                    <div className="mo-totals">
                      <div className="mo-total-item">
                        <div className="mo-total-label">Subtotal</div>
                        <div className="mo-total-val">${Number(order.subtotal || 0).toFixed(2)}</div>
                      </div>
                      {Number(order.discount) > 0 && (
                        <div className="mo-total-item">
                          <div className="mo-total-label">Discount</div>
                          <div className="mo-total-val mo-total-val--discount">
                            −${Number(order.discount).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {Number(order.tax) > 0 && (
                        <div className="mo-total-item">
                          <div className="mo-total-label">Tax</div>
                          <div className="mo-total-val">${Number(order.tax).toFixed(2)}</div>
                        </div>
                      )}
                      <div className="mo-total-item">
                        <div className="mo-total-label">Total</div>
                        <div className="mo-total-val mo-total-val--final">
                          ${Number(order.total || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
  .mo-root {
    font-family: 'Syne', sans-serif;
    color: #111827;
    width: min(100%, 860px);
    max-width: 100%;
    margin: 0 auto;
    padding-bottom: 40px;
    overflow-x: hidden;
  }
  .mo-root,
  .mo-root * {
    box-sizing: border-box;
    min-width: 0;
  }
  .mo-root img,
  .mo-root svg,
  .mo-root video,
  .mo-root canvas {
    max-width: 100%;
  }

  /* ── Topbar ── */
  .mo-topbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 28px 0; gap: 12px; flex-wrap: wrap;
  }
  .mo-back-btn {
    display: inline-flex; align-items: center; gap: 7px;
    background: none; border: none; cursor: pointer;
    font-family: 'Syne', sans-serif; font-size: 13px; font-weight: 500;
    color: #6b7280; padding: 0; transition: color .15s;
  }
  .mo-back-btn:hover { color: #111827; }
  .mo-back-btn .ph { font-size: 15px; }
  .mo-refresh-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 8px;
    border: 1px solid #e5e7eb; background: #fff;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600;
    color: #6b7280; cursor: pointer; transition: all .15s;
  }
  .mo-refresh-btn:hover { background: #f9fafb; color: #111827; }
  .mo-refresh-btn:disabled { opacity: .6; cursor: not-allowed; }
  .mo-refresh-btn .ph { font-size: 14px; }
  @keyframes mo-spin { to { transform: rotate(360deg); } }
  .mo-spin { display: inline-block; animation: mo-spin .7s linear infinite; }

  /* ── Page header ── */
  .mo-page-header { padding: 22px 28px 0; }
  .mo-page-title { font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -.4px; }
  .mo-page-sub { font-size: 13px; color: #9ca3af; margin-top: 4px; }

  /* ── Stats ── */
  .mo-stats {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 12px; padding: 20px 28px 0;
  }
  .mo-stat {
    background: #fff; border: 1px solid #f3f4f6; border-radius: 12px;
    padding: 16px; display: flex; flex-direction: column; gap: 12px;
    transition: border-color .2s;
    min-width: 0;
  }
  .mo-stat:hover { border-color: #e5e7eb; }
  .mo-stat__icon {
    width: 36px; height: 36px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; font-size: 16px;
  }
  .mo-stat__icon--blue   { background: #EFF6FF; color: #1D4ED8; }
  .mo-stat__icon--green  { background: #F0FDF4; color: #15803D; }
  .mo-stat__icon--amber  { background: #FFFBEB; color: #D97706; }
  .mo-stat__icon--purple { background: #F5F3FF; color: #6D28D9; }
  .mo-stat__val {
    font-family: 'JetBrains Mono', monospace; font-size: 22px; font-weight: 700;
    color: #111827; line-height: 1;
    overflow-wrap: anywhere;
  }
  .mo-stat__lbl { font-size: 11px; color: #9ca3af; font-weight: 500; margin-top: 4px; }

  /* ── Error ── */
  .mo-error-alert {
    margin: 16px 28px 0; padding: 12px 16px; border-radius: 8px;
    background: #FEF2F2; border: 1px solid #FCA5A5; color: #DC2626;
    font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 8px;
  }

  /* ── Section ── */
  .mo-section { padding: 20px 28px 0; }
  .mo-section__header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 14px; flex-wrap: wrap; gap: 8px;
  }
  .mo-section__title { font-size: 14px; font-weight: 700; color: #111827; }
  .mo-section__count { font-size: 12px; color: #9ca3af; margin-top: 2px; }

  /* ── Chip filters (desktop) ── */
  .mo-chips-wrap {
    display: flex; gap: 6px; overflow-x: auto;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
    margin-bottom: 16px; padding-bottom: 2px;
    max-width: 100%;
  }
  .mo-chips-wrap::-webkit-scrollbar { display: none; }
  .mo-chip {
    flex-shrink: 0; padding: 7px 14px; border-radius: 40px;
    border: 1px solid #e5e7eb; background: #fff;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600;
    color: #6b7280; cursor: pointer; transition: all .15s;
    display: flex; align-items: center; gap: 6px; white-space: nowrap;
  }
  .mo-chip:hover { border-color: #d1d5db; color: #374151; background: #f9fafb; }
  .mo-chip--active { background: #111827; border-color: #111827; color: #fff; }
  .mo-chip__count { font-size: 10px; padding: 1px 6px; border-radius: 20px; font-weight: 700; }
  .mo-chip--active .mo-chip__count { background: rgba(255,255,255,.2); color: #fff; }
  .mo-chip:not(.mo-chip--active) .mo-chip__count { background: #f3f4f6; color: #9ca3af; }

  /* ── Dropdown (mobile) ── */
  .mo-dropdown-wrap { display: none; margin-bottom: 16px; }
  .mo-dropdown { position: relative; width: 100%; }
  .mo-dropdown__btn {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    padding: 10px 14px; background: #fff; border: 1px solid #e5e7eb;
    border-radius: 10px; cursor: pointer; font-family: 'Syne', sans-serif;
    transition: border-color .15s; gap: 8px;
  }
  .mo-dropdown__btn:hover { border-color: #111827; }
  .mo-dropdown__left { display: flex; align-items: center; gap: 8px; }
  .mo-dropdown__label { font-size: 13px; font-weight: 600; color: #111827; }
  .mo-dropdown__count {
    background: #f3f4f6; border-radius: 20px; padding: 1px 7px;
    font-size: 11px; font-weight: 700; color: #374151;
  }
  .mo-dropdown__chevron { font-size: 14px; color: #9ca3af; transition: transform .2s; }
  .mo-dropdown__chevron--open { transform: rotate(180deg); }
  .mo-dropdown__panel {
    position: absolute; top: calc(100% + 6px); left: 0; right: 0;
    background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
    z-index: 200; box-shadow: 0 8px 32px rgba(0,0,0,.10); overflow: hidden;
  }
  .mo-dropdown__item {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    gap: 8px; padding: 10px 14px; background: none;
    border: none; border-bottom: 1px solid #f9fafb;
    cursor: pointer; font-family: 'Syne', sans-serif; font-size: 13px; font-weight: 500;
    color: #374151; transition: background .12s;
  }
  .mo-dropdown__item:last-child { border-bottom: none; }
  .mo-dropdown__item:hover { background: #f9fafb; }
  .mo-dropdown__item--active { background: #F0FDF4 !important; color: #15803D; font-weight: 700; }
  .mo-dropdown__item-left { display: flex; align-items: center; gap: 9px; }
  .mo-dropdown__item-right { display: flex; align-items: center; gap: 6px; }
  .mo-dropdown__item-count {
    border-radius: 20px; padding: 1px 7px; font-size: 11px; font-weight: 700;
    background: #f3f4f6; color: #6b7280;
  }
  .mo-dropdown__item-count--active { background: #DCFCE7; color: #15803D; }
  .mo-dropdown__check { font-size: 12px; color: #15803D; }
  .mo-status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
  .mo-status-dot--pending    { background: #D97706; }
  .mo-status-dot--processing { background: #2563EB; }
  .mo-status-dot--shipped    { background: #16A34A; }
  .mo-status-dot--delivered  { background: #15803D; }
  .mo-status-dot--cancelled  { background: #DC2626; }
  .mo-status-dot--refunded   { background: #7C3AED; }
  .mo-status-dot--failed     { background: #DC2626; }

  /* ── Spinner ── */
  .mo-spinner {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 56px 24px; gap: 14px;
    font-size: 13px; color: #9ca3af;
  }
  .mo-spinner__ring {
    width: 28px; height: 28px; border-radius: 50%;
    border: 2px solid #e5e7eb; border-top-color: #6b7280;
    animation: mo-spin .7s linear infinite;
  }

  /* ── Empty ── */
  .mo-empty {
    text-align: center; padding: 56px 24px;
    background: #fff; border: 1px solid #f3f4f6; border-radius: 12px;
  }
  .mo-empty__icon { font-size: 40px; color: #e5e7eb; margin-bottom: 12px; display: block; }
  .mo-empty__title { font-size: 15px; font-weight: 700; color: #374151; margin-bottom: 6px; }
  .mo-empty__sub { font-size: 13px; color: #9ca3af; }

  /* ── Order cards ── */
  .mo-card {
    background: #fff; border: 1px solid #EAECF0; border-radius: 12px;
    margin-bottom: 8px; overflow: hidden; transition: border-color .2s, box-shadow .2s;
    max-width: 100%;
    min-width: 0;
  }
  .mo-card:hover { border-color: #d1d5db; }
  .mo-card--expanded {
    border-color: #9FE1CB;
    /*
      IMPORTANT:
      Use an inset shadow instead of an outside spread shadow.
      Outside shadows can create visual horizontal overflow when a card is expanded
      near the mobile viewport edge.
    */
    box-shadow: inset 0 0 0 2px rgba(157,225,203,.38);
  }

  .mo-card__header {
    padding: 16px 20px; cursor: pointer; user-select: none;
    display: grid; grid-template-columns: minmax(0, auto) minmax(0, 1fr) auto;
    grid-template-rows: auto auto; column-gap: 16px; row-gap: 8px; align-items: center;
    min-width: 0;
  }
  .mo-card__order      { grid-column: 1; grid-row: 1; min-width: 0; }
  .mo-card__meta       { grid-column: 2; grid-row: 1; display: flex; gap: 16px; flex-wrap: wrap; min-width: 0; }
  .mo-card__total-wrap { grid-column: 3; grid-row: 1; display: flex; align-items: center; gap: 10px; min-width: 0; }
  .mo-card__badges     { grid-column: 1/-1; grid-row: 2; display: flex; gap: 6px; flex-wrap: wrap; align-items: center; min-width: 0; }

  .mo-field-label {
    font-size: 10px; font-weight: 700; color: #9ca3af;
    text-transform: uppercase; letter-spacing: .7px; margin-bottom: 3px;
  }
  .mo-order-id {
    font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600;
    color: #111827; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; max-width: 100%;
  }
  .mo-meta-val {
    font-size: 12px; font-weight: 500; color: #6b7280; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; max-width: 100%;
  }
  .mo-card__total {
    font-family: 'JetBrains Mono', monospace; font-size: 15px; font-weight: 700;
    color: #111827; white-space: nowrap;
    overflow-wrap: anywhere;
  }
  .mo-card__chevron { font-size: 16px; color: #9ca3af; flex-shrink: 0; transition: transform .2s; }
  .mo-card__chevron--open { transform: rotate(180deg); }

  /* ── Badges ── */
  .mo-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 9px; border-radius: 20px;
    font-size: 11px; font-weight: 700; letter-spacing: .2px;
    white-space: nowrap; border: 1px solid transparent;
  }
  .mo-badge .ph { font-size: 10px; }
  .mo-badge--pending    { background: #FFFBEB; color: #92400E; border-color: #FDE68A; }
  .mo-badge--processing { background: #EFF6FF; color: #1E40AF; border-color: #BFDBFE; }
  .mo-badge--shipped    { background: #F0FDF4; color: #166534; border-color: #BBF7D0; }
  .mo-badge--delivered  { background: #F0FDF4; color: #14532D; border-color: #86EFAC; }
  .mo-badge--cancelled  { background: #FEF2F2; color: #991B1B; border-color: #FCA5A5; }
  .mo-badge--refunded   { background: #F5F3FF; color: #5B21B6; border-color: #DDD6FE; }
  .mo-badge--failed     { background: #FEF2F2; color: #991B1B; border-color: #FCA5A5; }
  .mo-badge--paid       { background: #F0FDF4; color: #14532D; border-color: #86EFAC; }
  .mo-badge--unpaid     { background: #FEF2F2; color: #991B1B; border-color: #FCA5A5; }

  /* ── Card body ── */
  .mo-card__body { border-top: 1px solid #F3F4F6; }

  /* ════════════════════════════════════════════════
     VERTICAL STEPPER TRACKING TIMELINE
     No horizontal layout = no overflow on any screen
  ════════════════════════════════════════════════ */
  .mo-timeline {
    padding: 20px; background: #F9FAFB; border-bottom: 1px solid #F3F4F6;
  }

  /* Cancelled / failed / refunded alert box */
  .mo-tl-alert {
    display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px;
    border-radius: 8px; font-size: 13px; font-weight: 600; line-height: 1.4;
  }
  .mo-tl-alert--cancelled,
  .mo-tl-alert--failed  { background: #FEF2F2; border: 1px solid #FCA5A5; color: #991B1B; }
  .mo-tl-alert--refunded { background: #F5F3FF; border: 1px solid #DDD6FE; color: #5B21B6; }
  .mo-tl-alert .ph { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .mo-tl-alert__title { font-weight: 700; }
  .mo-tl-alert__sub   { font-size: 12px; font-weight: 500; margin-top: 3px; opacity: .85; }

  /* Stepper shell — vertical flex column */
  .mo-stepper { display: flex; flex-direction: column; }

  /* One step row */
  .mo-step { display: flex; gap: 14px; }

  /* Left column: dot + connector line */
  .mo-step__line-wrap {
    display: flex; flex-direction: column; align-items: center;
    flex-shrink: 0; width: 32px;
  }

  /* Circle dot */
  .mo-step__dot {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 13px;
    border: 2px solid #E5E7EB; background: #fff; color: #9ca3af;
    transition: all .3s; position: relative; z-index: 1;
  }
  .mo-step__dot--done   { background: #639922; border-color: #639922; color: #fff; }
  .mo-step__dot--active { background: #fff; border-color: #639922; color: #639922; }
  /* Pulse ring on active dot */
  .mo-step__dot--active::after {
    content: ''; position: absolute; inset: -4px; border-radius: 50%;
    border: 2px solid #C0DD97;
  }

  /* Vertical connector line between dots */
  .mo-step__connector {
    width: 2px; flex: 1; min-height: 12px;
    background: #E5E7EB; margin: 3px 0;
  }
  .mo-step__connector--filled { background: #C0DD97; }

  /* Right column: label + sub text */
  .mo-step__content { padding: 4px 0 18px; flex: 1; min-width: 0; }
  .mo-step__content--last { padding-bottom: 4px; }

  .mo-step__label {
    font-size: 13px; font-weight: 700; color: #9ca3af;
    line-height: 1; margin-bottom: 3px; transition: color .3s;
  }
  .mo-step__label--done,
  .mo-step__label--active { color: #111827; }

  .mo-step__sub { font-size: 11px; color: #9ca3af; font-weight: 500; }
  .mo-step__sub--active { color: #3B6D11; font-weight: 600; }

  /* Footer summary pill */
  .mo-tl-footer {
    margin-top: 8px; padding: 10px 12px; border-radius: 8px;
    background: #fff; border: 1px solid #F3F4F6;
    font-size: 12px; font-weight: 600;
    display: flex; align-items: center; gap: 8px;
  }
  .mo-tl-footer .ph { font-size: 14px; }
  .mo-tl-footer--success { color: #3B6D11; }
  .mo-tl-footer--info    { color: #185FA5; }
  .mo-tl-footer--warn    { color: #854F0B; }

  /* ── Detail grid ── */
  .mo-detail-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
    gap: 16px; padding: 18px 20px; border-bottom: 1px solid #F3F4F6;
    min-width: 0;
  }
  .mo-detail-val        { font-size: 13px; font-weight: 600; color: #111827; }
  .mo-detail-val--sm    { font-size: 12px; }
  .mo-detail-val--coupon{ font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #15803D; font-weight: 600; }
  .mo-detail-val--email { font-size: 11px; word-break: break-all; }
  .mo-detail-sub        { font-size: 11px; color: #9ca3af; margin-top: 2px; }

  /* ── Info blocks ── */
  .mo-block { padding: 16px 20px; border-bottom: 1px solid #F3F4F6; }
  .mo-block__label {
    font-size: 10px; font-weight: 700; color: #9ca3af;
    text-transform: uppercase; letter-spacing: .7px;
    margin-bottom: 8px; display: flex; align-items: center; gap: 5px;
  }
  .mo-block__label .ph { font-size: 10px; }
  .mo-info-box {
    background: #F9FAFB; border: 1px solid #F3F4F6; border-radius: 10px;
    padding: 12px 14px; font-size: 13px; color: #374151; line-height: 1.7;
    word-break: break-word; overflow-wrap: anywhere;
    max-width: 100%;
  }
  .mo-info-box__company { color: #9ca3af; }
  .mo-info-box--italic  { font-style: italic; color: #6b7280; }

  /* ── Shipment tracking box ── */
  .mo-shiptrack-box {
    background: #F9FAFB; border: 1px solid #F3F4F6; border-radius: 10px; padding: 14px;
    display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  }
  .mo-shiptrack-info        { min-width: 0; flex: 1 1 180px; }
  .mo-shiptrack-name        { font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 4px; word-break: break-word; }
  .mo-shiptrack-code        { font-size: 12px; color: #6b7280; }
  .mo-shiptrack-code__val   { color: #15803D; font-family: 'JetBrains Mono', monospace; word-break: break-all; }
  .mo-track-btn {
    padding: 9px 16px; border-radius: 8px; background: #639922; color: #fff; border: none;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: background .15s; flex: 0 1 auto;
    max-width: 100%;
  }
  .mo-track-btn:hover { background: #3B6D11; }
  .mo-track-btn .ph   { font-size: 14px; }
  .mo-track-error     { margin-top: 8px; font-size: 12px; color: #DC2626; font-weight: 600; }

  /* ── Items list ── */
  .mo-items-header {
    padding: 12px 20px 8px; font-size: 11px; font-weight: 700; color: #374151;
    text-transform: uppercase; letter-spacing: .7px;
    display: flex; align-items: center; gap: 6px;
    border-top: 1px solid #F3F4F6; border-bottom: 1px solid #F3F4F6;
  }
  .mo-items-header .ph { font-size: 12px; }
  .mo-item {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 20px; gap: 12px; border-bottom: 1px solid #F9FAFB; min-width: 0;
  }
  .mo-item:last-of-type { border-bottom: none; }
  .mo-item__left  { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }
  .mo-item__right { text-align: right; flex-shrink: 0; }
  .mo-item__icon  {
    width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
    background: #F3F4F6; display: flex; align-items: center; justify-content: center;
    font-size: 14px; color: #6b7280;
  }
  .mo-item__info    { min-width: 0; flex: 1; }
  .mo-item__name    { font-size: 13px; font-weight: 600; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mo-item__variant { font-size: 11px; color: #9ca3af; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mo-item__price   { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #111827; white-space: nowrap; }
  .mo-item__qty     { font-size: 11px; color: #9ca3af; margin-top: 2px; }

  /* ── Totals ── */
  .mo-totals {
    padding: 14px 20px; background: #F9FAFB;
    display: flex; justify-content: flex-end; gap: 24px; flex-wrap: wrap;
  }
  .mo-total-item        { text-align: right; min-width: 0; }
  .mo-total-label       { font-size: 11px; color: #9ca3af; font-weight: 600; margin-bottom: 2px; }
  .mo-total-val         { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #111827; overflow-wrap: anywhere; }
  .mo-total-val--discount { color: #15803D; }
  .mo-total-val--final    { font-size: 15px; color: #15803D; }


  /* ── Expansion overflow guard ──
     This prevents the expanded order dropdown/body from increasing the page width.
     The main causes are: outside shadows, grid/flex children, nowrap text,
     tracking codes, email addresses, and item rows with max-content sizing. */
  .mo-section,
  .mo-card,
  .mo-card__header,
  .mo-card__body,
  .mo-timeline,
  .mo-detail-grid,
  .mo-block,
  .mo-info-box,
  .mo-shiptrack-box,
  .mo-items-header,
  .mo-item,
  .mo-totals,
  .mo-dropdown,
  .mo-dropdown__panel {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .mo-card__body,
  .mo-timeline,
  .mo-detail-grid,
  .mo-block,
  .mo-info-box,
  .mo-shiptrack-box,
  .mo-items-header,
  .mo-item,
  .mo-totals {
    overflow-x: clip;
  }
  
  .mo-cancel-btn {
    width: 100%;
    padding: 10px 14px;
    border-radius: 8px;
    background: #dc2626;
    color: #fff;
    border: none;
    font-family: 'Syne', sans-serif;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .mo-cancel-btn:hover:not(:disabled) {
    background: #b91c1c;
  }

  .mo-cancel-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  @supports not (overflow: clip) {
    .mo-card__body,
    .mo-timeline,
    .mo-detail-grid,
    .mo-block,
    .mo-info-box,
    .mo-shiptrack-box,
    .mo-items-header,
    .mo-item,
    .mo-totals {
      overflow-x: hidden;
    }
  }

  .mo-card__body *,
  .mo-dropdown__panel * {
    min-width: 0;
    max-width: 100%;
  }

  .mo-tl-footer,
  .mo-tl-alert,
  .mo-tl-alert > div,
  .mo-step__content,
  .mo-detail-val,
  .mo-shiptrack-name,
  .mo-shiptrack-code,
  .mo-items-header,
  .mo-item__name,
  .mo-item__variant,
  .mo-total-val,
  .mo-dropdown__label,
  .mo-dropdown__item-left span {
    overflow-wrap: anywhere;
  }

  .mo-dropdown__item-left {
    min-width: 0;
    flex: 1 1 auto;
  }

  .mo-dropdown__item-left span {
    min-width: 0;
  }

  .mo-dropdown__item-right {
    flex: 0 0 auto;
  }

  /* ════════════════════════════════════════════════
     RESPONSIVE — mobile ≤639px
  ════════════════════════════════════════════════ */
  @media (max-width: 1036px) {
    .mo-topbar      { padding: 14px 16px 0; }
    .mo-page-header { padding: 16px 16px 0; }
    .mo-page-title  { font-size: 18px; }
    .mo-stats       { grid-template-columns: repeat(2, 1fr); gap: 8px; padding: 14px 16px 0; }
    .mo-stat        { padding: 12px; gap: 8px; }
    .mo-stat__val   { font-size: 18px; }
    .mo-stat__icon  { width: 30px; height: 30px; font-size: 14px; }
    .mo-section     { padding: 14px 16px 0; }
    .mo-chips-wrap  { display: none; }
    .mo-dropdown-wrap { display: block; }
    .mo-error-alert { margin: 14px 16px 0; }

    .mo-root {
      width: 100%;
      max-width: 100vw;
      overflow-x: hidden;
    }

    .mo-card {
      overflow: hidden;
      contain: layout paint;
    }

    .mo-card--expanded {
      box-shadow: inset 0 0 0 2px rgba(157,225,203,.38);
    }

    /* Card header reflow */
    .mo-card__header {
      padding: 12px 14px;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-rows: auto auto auto;
      column-gap: 10px;
      row-gap: 6px;
    }
    .mo-card__order      { grid-column: 1; grid-row: 1; min-width: 0; }
    .mo-card__total-wrap {
      grid-column: 2;
      grid-row: 1;
      justify-self: end;
      max-width: 42vw;
    }
    .mo-card__total-wrap > div {
      min-width: 0;
    }
    .mo-card__total {
      max-width: 34vw;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .mo-card__meta       { grid-column: 1/-1; grid-row: 2; gap: 10px; width: 100%; }
    .mo-card__badges     { grid-column: 1/-1; grid-row: 3; }

    /* Timeline — already vertical, just tighten padding */
    .mo-step__dot { width: 28px; height: 28px; font-size: 11px; }

    /* Detail, blocks, items */
    .mo-detail-grid  { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 14px; }
    .mo-block        { padding: 14px; }
    .mo-item         { padding: 10px 14px; }
    .mo-items-header { padding: 12px 14px 8px; }

    /* Totals */
    .mo-totals { gap: 14px; padding: 12px 14px; flex-direction: column; align-items: flex-end; }

    /* Shipment tracking */
    .mo-shiptrack-box { flex-direction: column; }
    .mo-track-btn     { width: 100%; justify-content: center; }
  }
  /* Extra-small phones: remove remaining two-column pressure */
  @media (max-width: 430px) {
    .mo-topbar {
      align-items: stretch;
    }
    .mo-back-btn,
    .mo-refresh-btn {
      min-height: 40px;
    }
    .mo-detail-grid {
      grid-template-columns: 1fr;
    }
    .mo-card__meta {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }
    .mo-item {
      align-items: flex-start;
    }
    .mo-item__right {
      min-width: 0;
      max-width: 100%;
      flex-shrink: 1;
    }
  }

  @media (max-width: 380px) {
    .mo-stats {
      grid-template-columns: 1fr;
    }
    .mo-card__header {
      grid-template-columns: 1fr;
    }
    .mo-card__order,
    .mo-card__total-wrap,
    .mo-card__meta,
    .mo-card__badges {
      grid-column: 1;
    }
    .mo-card__total-wrap {
      grid-row: 2;
      justify-self: start;
      width: 100%;
      max-width: 100%;
      justify-content: space-between;
    }
    .mo-card__total {
      max-width: 100%;
    }
    .mo-card__meta {
      grid-row: 3;
    }
    .mo-card__badges {
      grid-row: 4;
    }
    .mo-item {
      flex-direction: column;
      align-items: stretch;
    }
    .mo-item__right {
      text-align: left;
      display: flex;
      justify-content: space-between;
      gap: 10px;
    }
    .mo-totals {
      align-items: stretch;
    }
    .mo-total-item {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      text-align: left;
    }
  }

`;

export default MyOrders;